import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { CalendarRange, Camera, Lightbulb, Loader2, Plus } from "lucide-react";
import api from "../../lib/api";
import SeasonReviewPanel from "./SeasonReviewPanel";
import PhotoCheckList from "./PhotoCheckList";

const MONTHS = ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"];

function useJob(onDone) {
  const [job, setJob] = useState(null);
  const timer = useRef(null);
  useEffect(() => () => clearInterval(timer.current), []);
  const start = (jobId) => {
    setJob({ id: jobId, status: "running", progress: 0, message: "startet …" });
    clearInterval(timer.current);
    timer.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/admin/jobs/${jobId}`);
        setJob(data);
        if (data.status === "done" || data.status === "error") {
          clearInterval(timer.current);
          if (data.status === "done") onDone?.();
          else toast.error(data.error || "Job fehlgeschlagen");
        }
      } catch { /* keep polling */ }
    }, 2000);
  };
  return [job, start];
}

const JobLog = ({ job }) => {
  if (!job) return null;
  const rows = job.result?.plants || [];
  const errs = job.result?.errors || [];
  return (
    <div className="mt-3 text-xs space-y-1" data-testid="enrich-job-log">
      <div className="flex items-center gap-2">
        {job.status === "running" && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
        <span data-testid="enrich-job-status">{job.message} {job.status === "running" ? `(${job.progress || 0} %)` : ""}</span>
      </div>
      {rows.length > 0 && (
        <ul className="max-h-60 overflow-auto rounded-lg border p-2 space-y-1 bg-white" style={{ borderColor: "var(--wp-border)" }}>
          {rows.map((r, i) => (
            <li key={i}><b>{r.name}</b>: {r.error ? <span className="text-[var(--wp-toxic)]">{r.error}</span> : (r.changes.join(" · ") || "keine Änderung")}</li>
          ))}
          {errs.map((e, i) => <li key={`e${i}`} className="text-[var(--wp-toxic)]"><b>{e.name}</b>: {e.error}</li>)}
        </ul>
      )}
    </div>
  );
};

export default function PlantEnrichPanel({ onDone }) {
  const [job, startJob] = useJob(onDone);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [sugg, setSugg] = useState(null);
  const [picked, setPicked] = useState([]);
  const [loadingSugg, setLoadingSugg] = useState(false);
  const running = job?.status === "running";

  const runMode = async (mode, confirmText) => {
    if (!window.confirm(confirmText)) return;
    try {
      const { data } = await api.post("/admin/plants/enrich-async", { mode, plant_ids: [] });
      startJob(data.job_id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Start fehlgeschlagen"); }
  };

  const loadSugg = async () => {
    setLoadingSugg(true);
    setSugg(null);
    try {
      const { data } = await api.get(`/admin/plants/suggestions?month=${month}`);
      setSugg(data.suggestions);
      setPicked([]);
    } catch (e) { toast.error(e?.response?.data?.detail || "Vorschläge fehlgeschlagen"); }
    finally { setLoadingSugg(false); }
  };

  const createPicked = async () => {
    try {
      const { data } = await api.post("/admin/plants/from-suggestions-async", { names: picked });
      startJob(data.job_id);
      setSugg(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Anlegen fehlgeschlagen"); }
  };

  const btn = "inline-flex items-center gap-1.5 px-3 py-2 rounded-full border text-sm transition-colors hover:bg-[var(--wp-cream)] disabled:opacity-40";
  return (
    <div className="plant-card p-4 space-y-3" data-testid="plant-enrich-panel">
      <div className="overline">Saison, Fotos & neue Pflanzen</div>
      <div className="flex flex-wrap gap-2">
        <button className={btn} style={{ borderColor: "var(--wp-border)" }} disabled={running} data-testid="enrich-season-btn"
          onClick={() => runMode("season", "Saison aller Pflanzen per KI neu ermitteln? Die alte Saison wird gesichert.")}>
          <CalendarRange className="w-4 h-4" /> Saison neu ermitteln (alle)
        </button>
        <button className={btn} style={{ borderColor: "var(--wp-border)" }} disabled={running} data-testid="enrich-photos-btn"
          onClick={() => runMode("photos", "Echte Fotos von Wikimedia holen? Pflanzen mit KI-Bild bekommen ein echtes Hauptfoto, dazu Blatt, Blüte und Frucht in die Galerie.")}>
          <Camera className="w-4 h-4" /> Echte Fotos holen (Wikimedia)
        </button>
      </div>
      <div className="flex flex-wrap items-center gap-2 pt-1">
        <Lightbulb className="w-4 h-4 opacity-70" />
        <span className="text-sm">Fehlende Pflanzen für</span>
        <select value={month} onChange={(e) => setMonth(Number(e.target.value))} className="px-2 py-1.5 rounded-lg border text-sm bg-white" style={{ borderColor: "var(--wp-border)" }} data-testid="suggest-month-select">
          {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
        </select>
        <button className={btn} style={{ borderColor: "var(--wp-border)" }} disabled={loadingSugg || running} onClick={loadSugg} data-testid="suggest-load-btn">
          {loadingSugg ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Vorschläge holen
        </button>
      </div>
      {sugg && (
        <div className="space-y-1" data-testid="suggest-list">
          {sugg.length === 0 && <div className="text-sm opacity-70">Keine neuen Vorschläge.</div>}
          {sugg.map((s, i) => (
            <label key={s.name_de} className="flex items-start gap-2 text-sm cursor-pointer" data-testid={`suggest-item-${i}`}>
              <input type="checkbox" className="mt-1" checked={picked.includes(s.name_de)}
                onChange={(e) => setPicked(e.target.checked ? [...picked, s.name_de] : picked.filter((n) => n !== s.name_de))} />
              <span><b>{s.name_de}</b> <i className="opacity-70">{s.name_lat}</i> – {s.reason}</span>
            </label>
          ))}
          {sugg.length > 0 && (
            <button className={btn} style={{ borderColor: "var(--wp-border)" }} disabled={!picked.length || running} onClick={createPicked} data-testid="suggest-create-btn">
              <Plus className="w-4 h-4" /> {picked.length} Pflanze(n) mit KI anlegen
            </button>
          )}
        </div>
      )}
      <JobLog job={job} />
      <details className="pt-2 border-t" style={{ borderColor: "var(--wp-border)" }} data-testid="season-review-toggle">
        <summary className="text-sm cursor-pointer py-1">Saison prüfen (alt ↔ neu)</summary>
        <div className="pt-2"><SeasonReviewPanel /></div>
      </details>
      <details className="pt-2 border-t" style={{ borderColor: "var(--wp-border)" }} data-testid="photo-check-toggle">
        <summary className="text-sm cursor-pointer py-1">Foto-Check (automatisch geholte Fotos prüfen)</summary>
        <div className="pt-2"><PhotoCheckList /></div>
      </details>
    </div>
  );
}
